import * as React from 'react'
import type { SearchClient } from 'algoliasearch'
import { invariant } from 'utils/invariant'
import {
  Configure,
  Index,
  InstantSearch,
  useConnector,
  useInstantSearch,
} from 'react-instantsearch'
import type {
  AutocompleteConnectorParams,
  AutocompleteWidgetDescription,
} from 'instantsearch.js/es/connectors/autocomplete/connectAutocomplete'
import connectAutocomplete from 'instantsearch.js/es/connectors/autocomplete/connectAutocomplete'

import { defaultSearchClient } from './algolia'
import { algoliaTemplatesIndex } from './algolia-templates'
import { algoliaContentIndex } from './algolia-content'
import { InsightsMiddleware } from './insights-middleware'

interface AlgoliaProviderProps extends Omit<
  React.ComponentProps<typeof InstantSearch>,
  'searchClient'
> {
  searchClient?: SearchClient
}

export function AlgoliaProvider({
  children,
  indexName = algoliaContentIndex,
  searchClient = defaultSearchClient,
  ...props
}: AlgoliaProviderProps) {
  invariant(indexName, 'indexName is not set within <AlgoliaProvider />')

  return (
    <InstantSearch {...props} searchClient={searchClient} indexName={indexName}>
      {children}
      <InsightsMiddleware />
    </InstantSearch>
  )
}

export function AlgoliaIndex({
  children,
  indexName = algoliaContentIndex,
}: React.PropsWithChildren<{ indexName?: string }>) {
  invariant(indexName, 'indexName is not set within <AlgoliaIndex />')

  return <Index indexName={indexName}>{children}</Index>
}

export function NoResultsBoundary({
  children,
  fallback,
}: {
  children: React.ReactNode
  fallback: React.ReactNode
}) {
  const { results } = useInstantSearch()

  // The `__isArtificial` flag makes sure to not display the No Results message
  // when no hits have been returned yet.
  if (!results.__isArtificial && results.nbHits === 0) {
    return (
      <>
        {fallback}
        <div hidden>{children}</div>
      </>
    )
  }

  return <>{children}</>
}

export type UseAutocompleteProps = AutocompleteConnectorParams

export function useAutocomplete(props?: UseAutocompleteProps) {
  return useConnector<AutocompleteConnectorParams, AutocompleteWidgetDescription>(
    connectAutocomplete,
    props,
  )
}

export function useCategories() {
  const { uiState } = useInstantSearch()
  const { category, subcategory } = uiState?.[algoliaTemplatesIndex]?.menu || {}

  return { category, subcategory }
}

// eslint-disable-next-line no-barrel-files/no-barrel-files
// oxlint-disable-next-line cf-web/no-barrel-file
export { Configure }
